import ModalComponent from '../../../components/ModalComponent';
import TextFieldComponent from '../../../components/TextFieldComponent';
import JsonEditorComponent from '../../../components/JsonEditorComponent';
import React, { SyntheticEvent, useEffect, useState } from 'react';
import ButtonComponent from '../../../components/ButtonComponent';
import { Box, Grid2 } from '@mui/material';
import {
  IWebhookPayloadCreateProps,
  IWebhookPayloadSimpleProps,
} from '../../../services/IntegrationService/WebhookService/types';
import {
  useCreateWebhookPayloadAsync,
  useDeleteWebhookPayloadByIdAsync,
  useGetAllWebhookPayloadsByLicenseAsync,
  useGetWebhookPayloadByIdAsync,
  useUpdateWebhookPayloadByIdAsync,
} from '../../../services/IntegrationService/WebhookService/hooks/useMutation';
import { TabContext, TabList, TabPanel } from '@mui/lab';
import Tab from '@mui/material/Tab';
import TableComponent, {
  IColumnProps,
} from '../../../components/TableComponent';
import { Add } from '@mui/icons-material';
import ButtonActionsComponent from '../../../components/ButtonActionsComponent';
import ConfirmDeleteModalComponent from '../../../components/ConfirmDeleteModalComponent';
import TooltipComponent from '../../../components/TooltipComponent';

interface WebhookPayloadEditorModalProps {
  isVisible: boolean;
  handleClose: () => void;
}

const WebhookPayloadEditorModal = ({
  isVisible,
  handleClose,
}: WebhookPayloadEditorModalProps) => {
  const [webhookPayload, setWebhookPayload] =
    useState<IWebhookPayloadCreateProps>({
      name: '',
      payload: '',
    });
  const [tabValue, setTabValue] = useState('1');
  const [payloads, setPayloads] = useState<IWebhookPayloadSimpleProps[]>([]);
  const [isEnabledEditionTab, setIsEnabledEditionTab] = useState(false);

  const handleChange = (event: SyntheticEvent, newValue: string) => {
    setTabValue(newValue);
  };
  const [selectedItemToDelete, setSelectedItemToDelete] =
    useState<IWebhookPayloadSimpleProps>();
  const [isVisibleDeleteItemModal, setIsVisibleDeleteItemModal] =
    useState(false);

  const { mutate: mutateCreateWebhookPayloadAsync } =
    useCreateWebhookPayloadAsync();
  const { mutateAsync: mutateGetAllWebhookPayloadsByLicenseAsync } =
    useGetAllWebhookPayloadsByLicenseAsync();
  const { mutate: mutateUpdateWebhookPayloadByIdAsync } =
    useUpdateWebhookPayloadByIdAsync();
  const { mutateAsync: mutateDeleteWebhookPayloadByIdAsync } =
    useDeleteWebhookPayloadByIdAsync();

  const { mutateAsync: mutateGetWebhookPayloadByIdAsync } =
    useGetWebhookPayloadByIdAsync();

  const handleSave = () => {
    if (webhookPayload.name.length < 1) {
      return;
    }
    if (!webhookPayload.payload) {
      return;
    }
    if (!webhookPayload.id) {
      mutateCreateWebhookPayloadAsync(webhookPayload, {
        onSuccess: () => {
          setTabValue('1');
          getPayloads();
        },
      });
    } else {
      mutateUpdateWebhookPayloadByIdAsync(
        {
          data: webhookPayload,
          id: webhookPayload.id,
        },
        {
          onSuccess: () => {
            setTabValue('1');
            getPayloads();
          },
        },
      );
    }
  };

  const columns: IColumnProps[] = [
    {
      id: 'name',
      label: 'Nome',
      width: '50%',
      render: (value, rowIndex) => {
        return (
          <TooltipComponent
            title={getPayloadTooltipDescription(payloads[rowIndex].id)}
          >
            {value}
          </TooltipComponent>
        );
      },
    },
    {
      id: 'id',
      label: 'Ações',
      align: 'center',
      width: '0%',
      render: (id) => (
        <ButtonActionsComponent
          isWithDefaultOptionsList
          handleDelete={() => handleDeleteItem(Number(id))}
          handleEdit={() => handleEdit(Number(id))}
        />
      ),
    },
  ];

  const getPayloadTooltipDescription = (value?: number) => {
    let description = '';
    if (value) {
      const payload = payloads.find((v) => v.id == value);
      if (payload) {
        description = payload.payload;
      }
    }
    return description;
  };

  const handleDeleteItem = (id: number) => {
    if (payloads) setSelectedItemToDelete(payloads.find((d) => d.id === id));
    setIsVisibleDeleteItemModal(true);
  };

  const handleEdit = (id: number) => {
    setIsEnabledEditionTab(true);
    setTabValue('2');
    mutateGetWebhookPayloadByIdAsync(id).then(setWebhookPayload);
  };

  const handleCancelDeletion = () => {
    setIsVisibleDeleteItemModal(false);
    setSelectedItemToDelete(undefined);
  };

  const getPayloads = () => {
    mutateGetAllWebhookPayloadsByLicenseAsync().then(setPayloads);
  };

  const handleConfirmDeletion = () => {
    if (selectedItemToDelete?.id)
      mutateDeleteWebhookPayloadByIdAsync(selectedItemToDelete?.id).then(() => {
        setIsVisibleDeleteItemModal(false);
        getPayloads();
      });
  };

  const handleNewPayload = () => {
    setIsEnabledEditionTab(true);
    setTabValue('2');
  };

  useEffect(() => {
    getPayloads();
  }, []);

  useEffect(() => {
    if (tabValue == '1') {
      setWebhookPayload({} as IWebhookPayloadCreateProps);
      setIsEnabledEditionTab(false);
    }
  }, [tabValue]);

  return (
    <ModalComponent
      isOpen={isVisible}
      onClose={handleClose}
      title={'Editor de payloads'}
    >
      <TabContext value={tabValue}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <TabList onChange={handleChange}>
            <Tab label="Listagem" value="1" />
            <Tab label="Edição" value="2" disabled={!isEnabledEditionTab} />
          </TabList>
        </Box>
        <TabPanel value="1">
          <TableComponent
            rows={payloads}
            columns={columns}
            title={'Payloads'}
            additionalTitleComponent={
              <ButtonComponent
                startIcon={<Add />}
                onClick={() => handleNewPayload()}
              >
                Novo payload
              </ButtonComponent>
            }
          />
        </TabPanel>

        <TabPanel value="2">
          <Grid2 container key={webhookPayload.id}>
            <Grid2 size={12}>
              <TextFieldComponent
                label={'Nome'}
                value={webhookPayload.name}
                onChangeText={(name) => {
                  setWebhookPayload((oldStt) => ({ ...oldStt, name }));
                }}
              />
            </Grid2>
            <Grid2 size={12}>
              <JsonEditorComponent
                value={
                  webhookPayload.payload
                    ? JSON.parse(webhookPayload.payload)
                    : {}
                }
                onChange={(value) =>
                  setWebhookPayload((oldStt) => ({
                    ...oldStt,
                    payload: JSON.stringify(value),
                  }))
                }
              />
            </Grid2>
            <Grid2 size={12}>
              <ButtonComponent fullWidth onClick={() => handleSave()}>
                Salvar
              </ButtonComponent>
            </Grid2>
          </Grid2>
        </TabPanel>
      </TabContext>

      <ConfirmDeleteModalComponent
        isVisibleDeleteDialog={isVisibleDeleteItemModal}
        setIsVisibleDeleteDialog={setIsVisibleDeleteItemModal}
        title={'Excluir payload'}
        message={`Tem certeza que deseja excluir o payload "${selectedItemToDelete?.name}"?`}
        handleCancelDeletion={handleCancelDeletion}
        handleConfirmDeletion={handleConfirmDeletion}
      />
    </ModalComponent>
  );
};

export default WebhookPayloadEditorModal;
